import Box from 'components/shared/Box'
import Modal from 'components/shared/Modal'
import Gallery from 'components/shared/Gallery'
import Text from 'components/shared/Text'
import SinglePhoto from 'components/shared/Gallery/SinglePhoto'
import accommodationModalPhoto1 from 'assets/images/accommodation/bungalowModal1.jpg'
import accommodationModalPhoto2 from 'assets/images/accommodation/bungalowModal2.jpg'
import accommodationModalPhoto3 from 'assets/images/accommodation/bungalowModal3.jpg'
import accommodationModalPhoto1Mobile from 'assets/images/accommodation/bungalowModalPhoto1_mobile.jpg'
import accommodationModalPhoto2Mobile from 'assets/images/accommodation/bungalowModalPhoto2_mobile.jpg'
import accommodationModalPhoto3Mobile from 'assets/images/accommodation/bungalowModalPhoto3_mobile.jpg'
import iconCheck from 'assets/icons/check.svg'
import IconWithText from 'components/shared/IconWithText'
import { useResolutions } from 'store/resolutions'
import BungalowInfoBanner from './BungalowInfoBanner'
import { FormattedMessage, useIntl } from 'react-intl'
import {
    AmenitiesListWrapper,
    AdditionalServices,
    AccommodationModalBody,
} from './styles'

import {
    amenities,
    additionalServices,
    bungalowBannersData,
} from './AccommodationModalData'

interface AccommodationModalProps {
    isOpened: boolean
    closeModal: () => void
}

const AccommodationModal = ({
    isOpened,
    closeModal,
}: AccommodationModalProps) => {
    const { formatMessage } = useIntl()
    const { isMobile } = useResolutions()

    return (
        <Modal
            isOpened={isOpened}
            maxWidth={770}
            onClose={closeModal}
            isMobileFullscreen={true}
            testIdPrefix="accommo-about"
        >
            <>
                <Box pt={[16, 32]} pb={[16, 32]} pl={40}>
                    <Text variant="heading3">Suntago Village</Text>
                </Box>
                <AccommodationModalBody
                    pt={[24, 32]}
                    pl={[16, 40]}
                    pr={[16, 40]}
                    pb={[24, 32]}
                >
                    <Text variant="body" color="grayscale_900" pb={24}>
                        {formatMessage({
                            id: 'accommodation.bungalow_modal.teaser_copy',
                            defaultMessage:
                                'Suntago Village to idealne miejsce dla fanów dobrej zabawy i tropikalnego klimatu, którzy chcą dłużej cieszyć się pobytem w Suntago i wakacyjną atmosferą.',
                        })}
                    </Text>
                    <Gallery hasAlternativeLayout={true}>
                        <SinglePhoto
                            photoPath={
                                isMobile
                                    ? accommodationModalPhoto1Mobile
                                    : accommodationModalPhoto1
                            }
                            alt="massage"
                        />
                        <SinglePhoto
                            photoPath={
                                isMobile
                                    ? accommodationModalPhoto2Mobile
                                    : accommodationModalPhoto2
                            }
                            alt="essential oils"
                        />
                        <SinglePhoto
                            photoPath={
                                isMobile
                                    ? accommodationModalPhoto3Mobile
                                    : accommodationModalPhoto3
                            }
                            alt="body scrub"
                        />
                    </Gallery>
                    <Text variant="heading4" pt={48}>
                        <FormattedMessage
                            id="accommodation.bungalow_modal.facilities_section_caption"
                            defaultMessage="Udogodnienia"
                        />
                    </Text>
                    <Text variant="body" pt={8} pb={24} color="grayscale_700">
                        <FormattedMessage
                            id="accommodation.bungalow_modal.facilities_section_info"
                            defaultMessage="Wybierz Bungalow Duo, aby podwoić dostępne udogodnienia."
                        />
                    </Text>
                    <AmenitiesListWrapper
                        direction="column"
                        wrap="wrap"
                        gap={24}
                        justify="space-between"
                        isFullWidth={true}
                    >
                        {amenities.map((amenity) => (
                            <Box key={amenity.highlightedText.id}>
                                <IconWithText
                                    align="center"
                                    icon={iconCheck}
                                    iconAlt="checkmark icon"
                                    textHighlighted={formatMessage(
                                        amenity.highlightedText
                                    )}
                                />
                                {amenity.additionalText && (
                                    <Text
                                        variant="body"
                                        color="grayscale_700"
                                        pl={32}
                                    >
                                        {' ('}
                                        <FormattedMessage
                                            {...amenity.additionalText}
                                        />
                                        {')'}
                                    </Text>
                                )}
                            </Box>
                        ))}
                    </AmenitiesListWrapper>
                    <Text variant="heading4" pt={48} pb={24}>
                        <FormattedMessage
                            id="accommodation.bungalow_modal.additional_section_caption"
                            defaultMessage="Dodatkowe usługi"
                        />
                    </Text>
                    <AdditionalServices
                        gap={24}
                        direction="column"
                        justify="space-evenly"
                        wrap="wrap"
                        isFullWidth={true}
                    >
                        {additionalServices.map(
                            ({
                                highlightedText,
                                additionalText,
                                icon,
                                iconAlt,
                            }) => (
                                <IconWithText
                                    align="center"
                                    key={highlightedText.id}
                                    icon={icon}
                                    iconAlt={iconAlt}
                                    textHighlighted={formatMessage(
                                        highlightedText
                                    )}
                                    text={
                                        additionalText
                                            ? formatMessage(additionalText)
                                            : null
                                    }
                                    textColor="grayscale_700"
                                />
                            )
                        )}
                    </AdditionalServices>
                    <Text variant="heading4" pt={48}>
                        <FormattedMessage
                            id="accommodation.bungalow_modal.bungalows_section_caption"
                            defaultMessage="Bungalowy"
                        />
                    </Text>
                    <Text variant="body" pt={8} pb={24} color="grayscale_700">
                        <FormattedMessage
                            id="accommodation.bungalow_modal.bungalows_section_teaser"
                            defaultMessage="Oferujemy dwa rodzaje bungalowów. Standardowy Bungalow jest idealny dla rodziny lub paczki przyjaciół, a Bungalow Duo to świetna opcja dla większych grup."
                        />
                    </Text>
                    <Box
                        direction={['column', 'row']}
                        gap={[22, 24]}
                        isFullWidth={true}
                        justify="space-between"
                        align="center"
                    >
                        {bungalowBannersData.map(
                            ({
                                bungalowName,
                                maxGuests,
                                bannerVersion,
                                doubleBedNo,
                                singleBedNo,
                                sofaNo,
                                kitchenNo,
                                bathroomNo,
                                price,
                            }) => (
                                <BungalowInfoBanner
                                    key={bungalowName}
                                    maxGuests={maxGuests}
                                    bannerVersion={bannerVersion}
                                    bungalowName={bungalowName}
                                    doubleBedNo={doubleBedNo}
                                    singleBedNo={singleBedNo}
                                    sofaNo={sofaNo}
                                    kitchenNo={kitchenNo}
                                    bathroomNo={bathroomNo}
                                    price={price}
                                />
                            )
                        )}
                    </Box>
                    <Text variant="bodySmall" pt={16} color="grayscale_700">
                        <FormattedMessage
                            id="accommodation.bungalow_modal.legend_price_dependence"
                            defaultMessage="*Cena zależy od liczby osób i terminu. Dokończ rezerwację zakwaterowania, aby sprawdzić cenę."
                        />
                    </Text>
                </AccommodationModalBody>
            </>
        </Modal>
    )
}

export default AccommodationModal
